import Vue from 'vue'
import Router from 'vue-router'
import { getRouteAccessDecision } from '@/auth/access'
import { getLandingForRoles } from '@/config/roleSidebarMap'
import { decorateTitle } from '@/utils/envBadge'
import { recordVisit } from '@/utils/spotlightUsage'

// Containers
const TheContainer = () => import('@/containers/TheContainer')

// Views
const Dashboard = () => import('@/views/Dashboard')
const Analytics = () => import('@/views/Analytics')
const BiometricPinMapping = () => import('@/views/admin/BiometricPinMapping')
const AttendanceHoursReport = () => import('@/views/admin/AttendanceHoursReport')
const PayrollMonthlyAttendance = () => import('@/views/admin/PayrollMonthlyAttendance')
const MyAttendance = () => import('@/views/attendance/MyAttendance')
const AttendanceDevices = () => import('@/views/admin/AttendanceDevices')
const ShippingDashboard = () => import('@/views/shipping/ShippingDashboard')
const Barcodes = () => import('@/views/barcodes/Barcodes')
const ScannerReadyToShip = () => import('@/views/shipping/ScannerReadyToShip')
const ScannerReturns = () => import('@/views/shipping/ScannerReturns')
const FeedbackCall = () => import('@/views/calls/FeedbackCall')
const MDIFeedbackCall = () => import('@/views/calls/MDIFeedbackCall')
const CallQueue = () => import('@/views/calls/CallQueue')
const Installments = () => import('@/views/finance/Installments')
const VppPayments = () => import('@/views/finance/VppPayments')
const RazorpayTransactions = () => import('@/views/finance/RazorpayTransactions')
const CallLog = () => import('@/views/calls/CallLog')
const CallSummary = () => import('@/views/calls/CallSummary')
const MyCallSummary = () => import('@/views/calls/MyCallSummary')
const GradedFollowUps = () => import('@/views/calls/GradedFollowUps')
const HmsImports = () => import('@/views/incentives/HmsImports')
const IncentivePreview = () => import('@/views/incentives/IncentivePreview')
const AmazonCareOf = () => import('@/views/amazon/AmazonCareOf')
const FlipkartCareOf = () => import('@/views/flipkart/FlipkartCareOf')
const WebsiteCareOf = () => import('@/views/website/WebsiteCareOf')
const StoreCareOf = () => import('@/views/store/StoreCareOf')
const TeleSalesAccounts = () => import('@/views/incentives/TeleSalesAccounts')
const StockUpdates = () => import('@/views/operations/StockUpdates')
const DropdownItems = () => import('@/views/settings/DropdownItems')
const Settings = () => import('@/views/settings/Settings')
const SettingsRoles = () => import('@/views/settings/SettingsRoles')
const SettingsCreateRole = () => import('@/views/settings/SettingsCreateRole')
const SettingsInvoice = () => import('@/views/settings/SettingsInvoice')
const SettingsOrganisations = () => import('@/views/settings/SettingsOrganisations')
const SettingsCreateOrganisation = () => import('@/views/settings/SettingsCreateOrganisation')
const SettingsEditOrganisation = () => import('@/views/settings/SettingsEditOrganisation')
const SettingsDepartments = () => import('@/views/settings/SettingsDepartments')
const SettingsCreateDepartment = () => import('@/views/settings/SettingsCreateDepartment')
const SettingsEditDepartment = () => import('@/views/settings/SettingsEditDepartment')
const SettingsStates = () => import('@/views/settings/SettingsStates')
const SettingsBranches = () => import('@/views/settings/SettingsBranches')
const SettingsCallQueues = () => import('@/views/settings/SettingsCallQueues')
const SettingsQuickReplies = () => import('@/views/settings/SettingsQuickReplies')
const SettingsProducts = () => import('@/views/settings/SettingsProducts')
const SettingsPincodes = () => import('@/views/settings/SettingsPincodes')
const SettingsShipping = () => import('@/views/settings/SettingsShipping')
const SettingsDiscountPresets = () => import('@/views/settings/SettingsDiscountPresets')
const SettingsDomains = () => import('@/views/settings/SettingsDomains')
const SettingsDomainDns = () => import('@/views/settings/SettingsDomainDns')
const TallyExport = () => import('@/views/exports/TallyExport')
const ZohoExport = () => import('@/views/exports/ZohoExport')
const VerifyZohoInvoices = () => import('@/views/exports/VerifyZohoInvoices')
const FieldSalesForm = () => import('@/views/forms/FieldSalesForm')
const FieldVisits = () => import('@/views/crm/FieldVisits')
const TeleSales = () => import('@/views/forms/TeleSales')
const OrderForm = () => import('@/views/forms/OrderForm')
const POS = () => import('@/views/forms/POS')
const Orders = () => import('@/views/orders/Orders')
const MvpBulkOrders = () => import('@/views/orders/MvpBulkOrders.vue')
const MapBulkOrders = () => import('@/views/orders/MapBulkOrders.vue')
const CallingForm = () => import('@/views/forms/CallingForm')
const SalesReport = () => import('@/views/reports/SalesReport')
const PosSalesReport = () => import('@/views/reports/PosSalesReport')
const DepartmentReport = () => import('@/views/reports/DepartmentReport')
const BranchReport = () => import('@/views/reports/BranchReport')
const AgentwiseReport = () => import('@/views/reports/AgentwiseReport')
const TelesalesReport = () => import('@/views/reports/TelesalesReport')
const Leaderboard = () => import('@/views/Leaderboard')
const DownloadReports = () => import('@/views/reports/DownloadReports')
const PackingBatches = () => import('@/views/operations/PackingBatches')
const Refunds = () => import('@/views/refunds/Refunds')
const Customers = () => import('@/views/crm/Customers')
const TelesalesCustomers = () => import('@/views/crm/TelesalesCustomers')
const KarnatakaCustomers = () => import('@/views/crm/KarnatakaCustomers')
const Tickets = () => import('@/views/crm/Tickets')
const CustomerDetails = () => import('@/views/crm/CustomerDetails')
const AddPriorityNumber = () => import('@/views/crm/AddPriorityNumber')
const Inbox = () => import('@/views/inbox/Inbox')
const InfoCenter = () => import('@/views/info/InfoCenter')
const InfoDocument = () => import('@/views/info/InfoDocument')
const InfoEditor = () => import('@/views/info/InfoEditor')
const Vault = () => import('@/views/vault/Vault')
const VaultEntryForm = () => import('@/views/vault/VaultEntryForm')
const PrivateNotes = () => import('@/views/privateNotes/PrivateNotes')
const Calendar = () => import('@/views/calendar/Calendar')

const Colors = () => import('@/views/theme/Colors')
const Typography = () => import('@/views/theme/Typography')

const Charts = () => import('@/views/charts/Charts')
const Widgets = () => import('@/views/widgets/Widgets')

// Views - Components
const Cards = () => import('@/views/base/Cards')
const Forms = () => import('@/views/base/Forms')
const Switches = () => import('@/views/base/Switches')
const Tables = () => import('@/views/base/Tables')
const Tabs = () => import('@/views/base/Tabs')
const Breadcrumbs = () => import('@/views/base/Breadcrumbs')
const Carousels = () => import('@/views/base/Carousels')
const Collapses = () => import('@/views/base/Collapses')
const Jumbotrons = () => import('@/views/base/Jumbotrons')
const ListGroups = () => import('@/views/base/ListGroups')
const Navs = () => import('@/views/base/Navs')
const Navbars = () => import('@/views/base/Navbars')
const Paginations = () => import('@/views/base/Paginations')
const Popovers = () => import('@/views/base/Popovers')
const ProgressBars = () => import('@/views/base/ProgressBars')
const Tooltips = () => import('@/views/base/Tooltips')

// Views - Buttons
const StandardButtons = () => import('@/views/buttons/StandardButtons')
const ButtonGroups = () => import('@/views/buttons/ButtonGroups')
const Dropdowns = () => import('@/views/buttons/Dropdowns')
const BrandButtons = () => import('@/views/buttons/BrandButtons')

// Views - Icons
const CoreUIIcons = () => import('@/views/icons/CoreUIIcons')
const Brands = () => import('@/views/icons/Brands')
const Flags = () => import('@/views/icons/Flags')

// Views - Notifications
const Alerts = () => import('@/views/notifications/Alerts')
const Badges = () => import('@/views/notifications/Badges')
const Modals = () => import('@/views/notifications/Modals')

// Views - Pages
const Page404 = () => import('@/views/pages/Page404')
const Page403 = () => import('@/views/pages/Page403')
const Page500 = () => import('@/views/pages/Page500')
const Login = () => import('@/views/pages/Login')
const Register = () => import('@/views/pages/Register')

// Users
const Users = () => import('@/views/users/Users')
const User = () => import('@/views/users/User')
const EditUser = () => import('@/views/users/EditUser')

// Profile (self-service)
const ProfileSettings = () => import('@/views/profile/ProfileSettings')
const NetworkSpeed = () => import('@/views/NetworkSpeed')

//Notes
const Notes = () => import('@/views/notes/Notes')
const Note = () => import('@/views/notes/Note')
const EditNote = () => import('@/views/notes/EditNote')
const CreateNote = () => import('@/views/notes/CreateNote')

//Roles
const Roles = () => import('@/views/roles/Roles')
const Role = () => import('@/views/roles/Role')
const EditRole = () => import('@/views/roles/EditRole')
const CreateRole = () => import('@/views/roles/CreateRole')

//Bread
const Breads = () => import('@/views/bread/Breads')
const Bread = () => import('@/views/bread/Bread')
const EditBread = () => import('@/views/bread/EditBread')
const CreateBread = () => import('@/views/bread/CreateBread')
const DeleteBread = () => import('@/views/bread/DeleteBread')

//Resources
const Resources = () => import('@/views/resources/Resources')
const CreateResource = () => import('@/views/resources/CreateResources')
const Resource = () => import('@/views/resources/Resource')
const EditResource = () => import('@/views/resources/EditResource')
const DeleteResource = () => import('@/views/resources/DeleteResource')

//Email
const Emails = () => import('@/views/email/Emails')
const CreateEmail = () => import('@/views/email/CreateEmail')
const EditEmail = () => import('@/views/email/EditEmail')
const ShowEmail = () => import('@/views/email/ShowEmail')
const SendEmail = () => import('@/views/email/SendEmail')

const Menus = () => import('@/views/menu/MenuIndex')
const CreateMenu = () => import('@/views/menu/CreateMenu')
const EditMenu = () => import('@/views/menu/EditMenu')
const DeleteMenu = () => import('@/views/menu/DeleteMenu')

const MenuElements = () => import('@/views/menuElements/ElementsIndex')
const CreateMenuElement = () => import('@/views/menuElements/CreateMenuElement')
const EditMenuElement = () => import('@/views/menuElements/EditMenuElement')
const ShowMenuElement = () => import('@/views/menuElements/ShowMenuElement')
const DeleteMenuElement = () => import('@/views/menuElements/DeleteMenuElement')

const Media = () => import('@/views/media/Media')

// Websites (Manakkal Drugs Neon DB)
const WebsitesLayout = () => import('@/views/websites/WebsitesLayout')
const WebsitesDashboard = () => import('@/views/websites/Dashboard')
const WebsitesProducts = () => import('@/views/websites/Products')
const WebsitesArticles = () => import('@/views/websites/Articles')
const WebsitesNews = () => import('@/views/websites/News')
const WebsitesDivisions = () => import('@/views/websites/Divisions')
const WebsitesDoctors = () => import('@/views/websites/Doctors')
const WebsitesVideos = () => import('@/views/websites/Videos')
const WebsitesTimeline = () => import('@/views/websites/Timeline')


Vue.use(Router)

let router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
})


router.beforeEach((to, from, next) => {
  const decision = getRouteAccessDecision(to)

  if (!decision.allowed) {
    const redirect = {
      path: decision.redirectPath
    }

    if (decision.includeNextUrl) {
      redirect.params = { nextUrl: to.fullPath }
    }

    next(redirect)
    return
  }

  next()
})

router.afterEach((to) => {
  // Feeds the spotlight's Recent / Suggested groups. localStorage only — no
  // request, and it runs after navigation has already committed.
  recordVisit(to.path)

  const matched = to.matched.filter(r => r.meta && r.meta.label || r.name)
  const last = matched[matched.length - 1]
  const pageName = (last && (last.meta && last.meta.label || last.name)) || ''
  document.title = decorateTitle(pageName ? `OMS | ${pageName}` : 'OMS')
})

export default router

function configRoutes() {
  return [
    {
      path: '/',
      redirect: () => getLandingForRoles(localStorage.getItem('roles')),
      name: 'Home',
      component: TheContainer,
      children: [
        {
          path: 'media',
          name: 'Media',
          component: Media,
          meta: {
            requiresAdmin: true
          }
        },
        {
          path: 'attendance/pins',
          name: 'BiometricPinMapping',
          component: BiometricPinMapping,
          meta: {
            label: 'Biometric PIN → Staff',
            allowedRoleGroups: ['admin', 'internal_auditor'],
            requiresPermission: 'view_attendance'
          }
        },
        {
          path: 'attendance/hours',
          name: 'AttendanceHoursReport',
          component: AttendanceHoursReport,
          meta: {
            // sales_admin reaches this page too, but the API forces the branch
            // filter to their own branch — see AttendanceController.
            label: 'Attendance — Hours',
            allowedRoleGroups: ['admin', 'internal_auditor', 'sales_admin'],
            requiresPermission: 'view_attendance'
          }
        },
        {
          path: 'attendance/monthly',
          name: 'PayrollMonthlyAttendance',
          component: PayrollMonthlyAttendance,
          meta: {
            label: 'Payroll — Monthly Attendance',
            allowedRoleGroups: ['admin', 'internal_auditor'],
            requiresPermission: 'view_attendance'
          }
        },
        {
          path: 'attendance/devices',
          name: 'AttendanceDevices',
          component: AttendanceDevices,
          meta: {
            label: 'Attendance Devices',
            allowedRoleGroups: ['admin', 'internal_auditor'],
            requiresPermission: 'view_attendance'
          }
        },
        {
          // Self-service: open to any signed-in staff member (no role gate);
          // the API scopes results to the caller's own biometric PIN.
          path: 'attendance/my',
          name: 'MyAttendance',
          component: MyAttendance,
          meta: {
            label: 'My Attendance'
          }
        },
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard,
          meta: {
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'manager', 'moderator', 'sales_admin', 'sales_supervisor', 'accountant', 'junior_accountant', 'accounts_trainee', 'field_staff', 'operations', 'finance', 'ecom_manager', 'storekeeper', 'telesales']
          }
        },
        {
          path: 'analytics',
          name: 'Analytics',
          component: Analytics,
          meta: {
            label: 'Analytics',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'accountant'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'profile-settings',
          name: 'ProfileSettings',
          component: ProfileSettings,
          meta: {
            label: 'Profile Settings'
          }
        },
        {
          path: 'network-speed',
          name: 'NetworkSpeed',
          component: NetworkSpeed,
          meta: {
            label: 'Network Speed'
          }
        },
        {
          path: 'calendar',
          name: 'Calendar',
          component: Calendar,
          meta: {
            label: 'Calendar'
          }
        },
        {
          path: 'shipping/dashboard',
          name: 'ShippingDashboard',
          component: ShippingDashboard,
          meta: {
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'accounts_trainee', 'packer', 'storekeeper'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'barcodes',
          name: 'Barcodes',
          component: Barcodes,
          meta: {
            label: 'Barcodes',
            description: 'Inventory of pre-printed courier article numbers and which ones have been used as tracking IDs',
            // Mirrors BarcodeController::VIEW_ROLES. Upload/void/delete are
            // gated separately inside the page (and again on the server) to
            // BarcodeController::MANAGE_ROLES.
            allowedRoleGroups: [
              'admin', 'moderator', 'manager', 'sales_admin', 'sales_supervisor',
              'internal_auditor', 'accountant', 'senior_accountant', 'junior_accountant',
              'accounts_trainee', 'storekeeper', 'packer', 'operations'
            ],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'shipping/scanner',
          name: 'ScannerReadyToShip',
          component: ScannerReadyToShip,
          meta: {
            label: 'Ready To Ship Scanner',
            allowedRoleGroups: ['user', 'admin', 'sales_admin', 'sales_supervisor', 'packer', 'storekeeper'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'shipping/returns',
          name: 'ScannerReturns',
          component: ScannerReturns,
          meta: {
            label: 'Mark Returns',
            allowedRoleGroups: ['admin', 'moderator', 'sales_admin', 'sales_supervisor', 'accountant'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'websites',
          component: WebsitesLayout,
          meta: { label: 'Websites', requiresAdmin: true },
          children: [
            { path: '', name: 'WebsitesDashboard', component: WebsitesDashboard, meta: { label: 'Websites Dashboard', requiresAdmin: true } },
            { path: 'products', name: 'WebsitesProducts', component: WebsitesProducts, meta: { label: 'Websites · Products', requiresAdmin: true } },
            { path: 'articles', name: 'WebsitesArticles', component: WebsitesArticles, meta: { label: 'Websites · Articles', requiresAdmin: true } },
            { path: 'news', name: 'WebsitesNews', component: WebsitesNews, meta: { label: 'Websites · News', requiresAdmin: true } },
            { path: 'divisions', name: 'WebsitesDivisions', component: WebsitesDivisions, meta: { label: 'Websites · Divisions', requiresAdmin: true } },
            { path: 'doctors', name: 'WebsitesDoctors', component: WebsitesDoctors, meta: { label: 'Websites · Doctors', requiresAdmin: true } },
            { path: 'videos', name: 'WebsitesVideos', component: WebsitesVideos, meta: { label: 'Websites · Videos', requiresAdmin: true } },
            { path: 'timeline', name: 'WebsitesTimeline', component: WebsitesTimeline, meta: { label: 'Websites · Timeline', requiresAdmin: true } },
          ],
        },
        {
          path: 'callqueue',
          name: 'CallQueue',
          component: CallQueue,
          meta: { label: 'Call Queue' }
        },
        {
          path: 'feedback_call',
          name: 'FeedbackCall',
          component: FeedbackCall,
          meta: {
            label: 'Feedback Call',
            requiresAdmin: true
          }
        },
        {
          path: 'mdi_feedback_call',
          name: 'MDIFeedbackCall',
          component: MDIFeedbackCall,
          meta: {
            label: 'MDI Feedback Call',
            requiresAdmin: true
          }
        },
        {
          path: 'installments',
          name: 'Installments',
          component: Installments,
          meta: {
            label: 'Installments',
            requiresAdmin: true
          }
        },
        {
          path: 'callsummary',
          name: 'CallSummary',
          component: CallSummary,
          meta: { label: 'Call Summary' }
        },
        {
          path: 'my_call_summary',
          name: 'MyCallSummary',
          component: MyCallSummary,
          meta: {
            label: 'My Call Summary',
            allowedRoleGroups: ['telesales', 'admin', 'sales_admin', 'sales_supervisor']
          }
        },
        {
          path: 'call_log',
          name: 'CallLog',
          component: CallLog,
          meta: {
            label: 'Call Log',
            requiresAdmin: true
          }
        },
        {
          path: 'grade_followups',
          name: 'GradedFollowUps',
          component: GradedFollowUps,
          meta: {
            label: 'Graded Follow Ups',
            allowedRoleGroups: ['admin', 'telesales', 'sales_admin', 'sales_supervisor']
          }
        },
        {
          path: 'hms_imports',
          name: 'HmsImports',
          component: HmsImports,
          meta: {
            label: 'HMS Imports',
            allowedRoleGroups: ['admin', 'superadmin', 'biller']
          }
        },
        {
          path: 'incentive_preview',
          name: 'IncentivePreview',
          component: IncentivePreview,
          meta: {
            label: 'Incentive Preview',
            requiresAdmin: true
          }
        },
        {
          path: 'amazon_care_of',
          name: 'AmazonCareOf',
          component: AmazonCareOf,
          meta: {
            label: 'Amazon Care-of Verification',
            requiresAdmin: true
          }
        },
        {
          path: 'flipkart_care_of',
          name: 'FlipkartCareOf',
          component: FlipkartCareOf,
          meta: {
            label: 'Flipkart Care-of Verification',
            requiresAdmin: true
          }
        },
        {
          path: 'website_care_of',
          name: 'WebsiteCareOf',
          component: WebsiteCareOf,
          meta: {
            label: 'Website Care-of Verification',
            requiresAdmin: true
          }
        },
        {
          path: 'store_care_of',
          name: 'StoreCareOf',
          component: StoreCareOf,
          meta: {
            label: 'Store Sales Care-of Verification',
            requiresAdmin: true
          }
        },
        {
          path: 'tele_sales_accounts',
          name: 'TeleSalesAccounts',
          component: TeleSalesAccounts,
          meta: {
            label: 'Tele-Sales Accounts',
            requiresAdmin: true
          }
        },
        {
          path: 'stock',
          name: 'StockUpdates',
          component: StockUpdates,
          meta: {
            label: 'Stock Updates',
            requiresAdmin: true
          }
        },
        {
          path: 'packingBatches',
          name: 'PackingBatches',
          component: PackingBatches,
          meta: {
            label: 'Packing Batches',
            allowedRoleGroups: ['admin', 'packer', 'storekeeper'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'pendingpayments',
          name: 'VppPayments',
          component: VppPayments,
          meta: {
            label: 'COD Payments',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'razorpay-transactions',
          name: 'RazorpayTransactions',
          component: RazorpayTransactions,
          meta: {
            label: 'Razorpay Transactions',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'addpriority',
          name: 'AddPriorityNumber',
          component: AddPriorityNumber,
          meta: {
            label: 'Add Priority Number',
            requiresAdmin: true
          }
        },
        {
          path: 'dropdownitems',
          name: 'DropdownItems',
          component: DropdownItems,
          meta: {
            label: 'Dropdown Items',
            requiresAdmin: true
          }
        },
        {
          path: 'tallyexport',
          name: 'TallyExport',
          component: TallyExport,
          meta: {
            label: 'Tally Export',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'zohoexport',
          name: 'ZohoExport',
          component: ZohoExport,
          meta: {
            label: 'Zoho Export',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'Verifyzohoinvoices',
          name: 'VerifyZohoInvoices',
          component: VerifyZohoInvoices,
          meta: {
            label: 'Verify Zoho Invoices',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'fieldsalesform',
          name: 'FieldSalesForm',
          component: FieldSalesForm,
          meta: {
            label: 'Field Sales Form',
            allowedRoleGroups: ['field_staff', 'manager', 'admin'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'fieldvisits',
          name: 'FieldVisits',
          component: FieldVisits,
          meta: {
            label: 'Field Visits',
            allowedRoleGroups: ['field_staff', 'manager', 'admin']
          }
        },
        {
          path: 'telesales',
          name: 'TeleSales',
          component: TeleSales,
          meta: {
            label: 'Tele Sales',
            // Tele staff have role 'telesales' (not 'user'), and the
            // ROLE_GROUPS.user group doesn't include 'telesales'. Use the
            // allowedRoleGroups list with literal role names so this form
            // is accessible from the Call Queue's Call button without
            // bouncing to /dashboard. 'admin' here picks up admin/
            // superadmin via the existing group mapping.
            allowedRoleGroups: ['telesales', 'admin', 'sales_admin', 'sales_supervisor']
          }
        },
        {
          path: 'orderform',
          name: 'OrderForm',
          component: OrderForm,
          meta: {
            label: 'Order Form',
            allowedRoleGroups: ['admin', 'storekeeper', 'sales_admin', 'sales_supervisor', 'accountant']
          }
        },
        {
          path: 'pos',
          name: 'POS',
          component: POS,
          meta: {
            label: 'Point of Sale',
            allowedRoleGroups: ['admin', 'storekeeper', 'biller', 'sales_admin', 'sales_supervisor'],
            blockedRoles: ['hr_manager'],
            fullscreen: true
          }
        },
        {
          path: 'orders',
          name: 'Orders',
          component: Orders,
          meta: {
            label: 'Orders',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'telesales', 'packer', 'field_staff', 'storekeeper', 'biller', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'mvpbulk',
          name: 'MvpBulkOrders',
          component: MvpBulkOrders,
          meta: {
            label: 'MVP Bulk Orders',
            requiresAdmin: true
          }
        },
        {
          path: 'mapbulk',
          name: 'MapBulkOrders',
          component: MapBulkOrders,
          meta: {
            label: 'MAP Bulk Orders',
            requiresAdmin: true
          }
        },
        {
          path: 'calling_form',
          name: 'CallingForm',
          component: CallingForm,
          meta: {
            label: 'Calling Form',
            requiresAdmin: true
          }
        },
        {
          path: 'salesreport',
          name: 'SalesReport',
          component: SalesReport,
          meta: {
            label: 'Sales Report',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'pos-sales',
          name: 'PosSalesReport',
          component: PosSalesReport,
          meta: {
            label: 'POS Sales',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'storekeeper', 'biller', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'departmentreport',
          name: 'DepartmentReport',
          component: DepartmentReport,
          meta: {
            label: 'Department Report',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'branchreport',
          name: 'BranchReport',
          component: BranchReport,
          meta: {
            label: 'Branch Report',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'agentwise_report',
          name: 'AgentwiseReport',
          component: AgentwiseReport,
          meta: {
            label: 'Agentwise Report',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'telesales_report',
          name: 'TelesalesReport',
          component: TelesalesReport,
          meta: {
            label: 'Telesales Report',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'leaderboard',
          name: 'Leaderboard',
          component: Leaderboard,
          meta: {
            label: 'Telesales Leaderboard',
            allowedRoleGroups: ['telesales', 'admin']
          }
        },
        {
          path: 'reports',
          name: 'DownloadReports',
          component: DownloadReports,
          meta: {
            label: 'Download Reports',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'moderator', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'refund',
          name: 'Refunds',
          component: Refunds,
          meta: {
            label: 'Refunds',
            allowedRoleGroups: ['admin', 'internal_auditor', 'senior_accountant', 'sales_admin', 'sales_supervisor', 'junior_accountant', 'accounts_trainee', 'accountant']
          }
        },
        {
          path: 'inbox',
          name: 'Inbox',
          component: Inbox,
          meta: {
            label: 'Inbox',
            allowedRoleGroups: ['admin', 'accountant'],
            blockedRoles: ['hr_manager']
          }
        },
        {
          path: 'info-center',
          name: 'InfoCenter',
          component: InfoCenter,
          meta: { label: 'Info Center' }
        },
        {
          path: 'info-center/new',
          name: 'CreateInfoDocument',
          component: InfoEditor,
          meta: {
            label: 'New Info Document',
            allowedRoleGroups: ['admin', 'hr_manager'],
            fullscreen: true
          }
        },
        {
          path: 'info-center/:id(\\d+)/edit',
          name: 'EditInfoDocument',
          component: InfoEditor,
          meta: {
            label: 'Edit Info Document',
            allowedRoleGroups: ['admin', 'hr_manager'],
            fullscreen: true
          }
        },
        {
          path: 'info-center/:slug',
          name: 'InfoDocument',
          component: InfoDocument,
          meta: { label: 'Info Document' }
        },
        {
          path: 'vault',
          name: 'Vault',
          component: Vault,
          meta: {
            label: 'Credentials Vault',
            allowedRoleGroups: ['superadmin'],
          }
        },
        {
          path: 'vault/new',
          name: 'CreateVaultEntry',
          component: VaultEntryForm,
          meta: {
            label: 'New Vault Entry',
            allowedRoleGroups: ['superadmin'],
          }
        },
        {
          path: 'vault/:id(\\d+)',
          name: 'EditVaultEntry',
          component: VaultEntryForm,
          meta: {
            label: 'Edit Vault Entry',
            allowedRoleGroups: ['superadmin'],
          }
        },
        {
          path: 'private-notes',
          name: 'PrivateNotes',
          component: PrivateNotes,
          meta: { label: 'Private Notes' }
        },
        {
          path: 'customers',
          name: 'Customers',
          component: Customers,
          meta: { label: 'Customers', requiresAdmin: true }
        },
        {
          // Advanced customer explorer for tele-sales staff: cross-org list
          // with crop / animal / medicine / tag / bulk filters, call + history.
          path: 'telesales-customers',
          name: 'TelesalesCustomers',
          component: TelesalesCustomers,
          meta: {
            label: 'Customers',
            allowedRoleGroups: ['telesales', 'admin', 'superadmin', 'manager', 'moderator', 'sales_admin', 'sales_supervisor']
          }
        },
        {
          // Restricted page: visible/accessible only to a single staff member
          // (user id 84). The nav item is injected for that user in
          // TheSidebar; this guard is the matching server-of-truth so a
          // direct URL hit by anyone else bounces to their landing page.
          path: 'karnataka-customers',
          name: 'KarnatakaCustomers',
          component: KarnatakaCustomers,
          meta: { label: 'Karnataka Customers' },
          beforeEnter: (to, from, next) => {
            let userId = null
            try {
              const raw = localStorage.getItem('auth_user')
              const user = raw ? JSON.parse(raw) : null
              userId = user ? Number.parseInt(user.id, 10) : null
            } catch (e) {
              userId = null
            }
            if (userId === 84) {
              next()
            } else {
              next(getLandingForRoles(localStorage.getItem('roles')))
            }
          }
        },
        {
          path: 'tickets',
          name: 'Tickets',
          component: Tickets,
          meta: { label: 'Tickets', requiresAdmin: true }
        },
        {
          path: 'alltickets',
          name: 'AllTickets',
          component: Tickets,
          meta: { label: 'All Tickets', requiresAdmin: true }
        },
        {
          path: 'customer/:id/:tab?',
          name: 'CustomerDetails',
          component: CustomerDetails,
          meta: {
            label: 'Customer Details',
            requiresAdmin: true
          }
        },
        {
          path: 'register',
          name: 'CreateNewUser',
          component: Register,
          meta: {
            label: 'Create New User',
            requiresAdmin: true
          }
        },
        {
          path: 'colors',
          name: 'Colors',
          component: Colors,
          meta: {
            requiresUser: true
          }
        },
        {
          path: 'typography',
          name: 'Typography',
          component: Typography,
          meta: {
            requiresUser: true
          }
        },
        {
          path: 'charts',
          name: 'Charts',
          component: Charts,
          meta: {
            requiresUser: true
          }
        },
        {
          path: 'widgets',
          name: 'Widgets',
          component: Widgets,
          meta: {
            requiresUser: true
          }
        },
        {
          path: 'menu',
          meta: { label: 'Menu' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Menus,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: 'create',
              name: 'CreateMenu',
              component: CreateMenu,
              meta: {
                label: 'Create Menu',
                requiresAdmin: true
              }
            },
            {
              path: ':id/edit',
              name: 'EditMenu',
              component: EditMenu,
              meta: {
                label: 'Edit Menu',
                requiresAdmin: true
              }
            },
            {
              path: ':id/delete',
              name: 'DeleteMenu',
              component: DeleteMenu,
              meta: {
                label: 'Delete Menu',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          path: 'menuelement',
          meta: { label: 'MenuElement' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: ':menu/menuelement',
              component: MenuElements,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: ':menu/menuelement/create',
              name: 'Create Menu Element',
              component: CreateMenuElement,
              meta: {
                label: 'Create Menu Element',
                requiresAdmin: true
              }
            },
            {
              path: ':menu/menuelement/:id',
              name: 'Menu Element',
              component: ShowMenuElement,
              meta: {
                label: 'Menu Element Details',
                requiresAdmin: true
              }
            },
            {
              path: ':menu/menuelement/:id/edit',
              name: 'Edit Menu Element',
              component: EditMenuElement,
              meta: {
                label: 'Edit Menu Element',
                requiresAdmin: true
              }
            },
            {
              path: ':menu/menuelement/:id/delete',
              name: 'Delete Menu Element',
              component: DeleteMenuElement,
              meta: {
                label: 'Delete Menu Element',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          path: 'users',
          meta: { label: 'Users' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Users,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: ':id',
              name: 'User',
              component: User,
              meta: {
                label: 'User Details',
                requiresAdmin: true
              }
            },
            {
              path: ':id/edit',
              name: 'Edit User',
              component: EditUser,
              meta: {
                label: 'Edit User',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          // Scoped settings shell for hr_manager: reuses the Settings.vue chrome
          // but its nav is filtered to the Users section only (Settings.vue keys
          // off the /staff path). Gated for hr_manager + admins and deliberately
          // NOT behind requiresPermission:'manage_settings', so it stays reachable
          // while the real /settings tree stays blocked for hr_manager (which
          // lacks that permission).
          path: 'staff',
          component: Settings,
          meta: { label: 'Staff', allowedRoleGroups: ['admin', 'hr_manager'] },
          children: [
            {
              path: '',
              redirect: { name: 'StaffUsers' }
            },
            {
              path: 'users',
              name: 'StaffUsers',
              component: Users,
              meta: { label: 'Users', allowedRoleGroups: ['admin', 'hr_manager'] }
            },
            {
              // Pincode master, scoped into the hr_manager /staff shell. Reuses
              // the same SettingsPincodes component as /settings/pincodes but is
              // gated by role group (not requiresPermission:'manage_settings')
              // so hr_manager can reach it while the full /settings tree stays
              // locked. Keep in sync with the staff-scope nav in Settings.vue.
              path: 'pincodes',
              name: 'StaffPincodes',
              component: SettingsPincodes,
              meta: { label: 'Pincodes', allowedRoleGroups: ['admin', 'hr_manager'] }
            },
            {
              // Quick replies (canned replies + product details) scoped into the
              // hr_manager /staff shell. Same SettingsQuickReplies component as
              // /settings/quick-replies, gated by role group instead of
              // requiresPermission:'manage_settings'. Keep in sync with the
              // staff-scope nav in Settings.vue.
              path: 'quick-replies',
              name: 'StaffQuickReplies',
              component: SettingsQuickReplies,
              meta: { label: 'Quick Replies', allowedRoleGroups: ['admin', 'hr_manager'] }
            },
          ]
        },
        {
          path: 'settings',
          component: Settings,
          // requiresPermission applies to every /settings/* child (it aggregates
          // across matched route records), so this one line locks the whole
          // Settings section to holders of 'manage_settings' — admin + superadmin
          // only. finance_manager is admin-equivalent for role groups but lacks
          // this permission, so it is bounced from all of Settings.
          meta: { label: 'Settings', requiresAdmin: true, requiresPermission: 'manage_settings' },
          children: [
            {
              path: '',
              redirect: { name: 'SettingsUsers' }
            },
            {
              path: 'users',
              name: 'SettingsUsers',
              component: Users,
              meta: { label: 'Users', requiresAdmin: true }
            },
            {
              path: 'organisations',
              name: 'SettingsOrganisations',
              component: SettingsOrganisations,
              meta: { label: 'Organisations', requiresAdmin: true }
            },
            {
              path: 'organisations/create',
              name: 'SettingsCreateOrganisation',
              component: SettingsCreateOrganisation,
              meta: { label: 'Create Organisation', requiresAdmin: true }
            },
            {
              path: 'organisations/:id/edit',
              name: 'SettingsEditOrganisation',
              component: SettingsEditOrganisation,
              meta: { label: 'Edit Organisation', requiresAdmin: true }
            },
            {
              path: 'roles',
              name: 'SettingsRoles',
              component: SettingsRoles,
              meta: { label: 'Roles', requiresAdmin: true }
            },
            {
              path: 'roles/create',
              name: 'SettingsCreateRole',
              component: SettingsCreateRole,
              meta: { label: 'Create Role', requiresAdmin: true }
            },
            {
              path: 'departments',
              name: 'SettingsDepartments',
              component: SettingsDepartments,
              meta: { label: 'Departments', requiresAdmin: true }
            },
            {
              path: 'departments/create',
              name: 'SettingsCreateDepartment',
              component: SettingsCreateDepartment,
              meta: { label: 'Create Department', requiresAdmin: true }
            },
            {
              path: 'departments/:id/edit',
              name: 'SettingsEditDepartment',
              component: SettingsEditDepartment,
              meta: { label: 'Edit Department', requiresAdmin: true }
            },
            {
              path: 'states',
              name: 'SettingsStates',
              component: SettingsStates,
              meta: { label: 'States', requiresAdmin: true }
            },
            {
              path: 'branches',
              name: 'SettingsBranches',
              component: SettingsBranches,
              meta: { label: 'Branches', requiresAdmin: true }
            },
            {
              path: 'invoice',
              name: 'SettingsInvoice',
              component: SettingsInvoice,
              meta: {
                label: 'Invoice',
                description: 'Configure invoice and order number prefixes and counters',
                requiresAdmin: true
              }
            },
            {
              path: 'call-queues',
              name: 'SettingsCallQueues',
              component: SettingsCallQueues,
              meta: {
                label: 'Call Queues',
                description: 'Manage call queues and routing',
                requiresAdmin: true
              }
            },
            {
              path: 'quick-replies',
              name: 'SettingsQuickReplies',
              component: SettingsQuickReplies,
              meta: {
                label: 'Quick Replies',
                description: 'Manage canned replies and product details for tele-sales staff',
                requiresAdmin: true
              }
            },
            {
              path: 'products',
              name: 'SettingsProducts',
              component: SettingsProducts,
              meta: {
                label: 'Products',
                description: 'Manage the product catalogue for orders, packing and stock',
                requiresAdmin: true
              }
            },
            {
              path: 'pincodes',
              name: 'SettingsPincodes',
              component: SettingsPincodes,
              meta: {
                label: 'Pincodes',
                description: 'Manage pincode and post-office records used for address lookups',
                requiresAdmin: true
              }
            },
            {
              path: 'shipping',
              name: 'SettingsShipping',
              component: SettingsShipping,
              meta: {
                label: 'Shipping & COD',
                description: 'Set the flat COD fee added to shipping for COD orders',
                requiresAdmin: true
              }
            },
            {
              path: 'discount-presets',
              name: 'SettingsDiscountPresets',
              component: SettingsDiscountPresets,
              meta: {
                label: 'Discount Presets',
                description: 'Per-organisation discount tiers that drive the OrderForm eligible-discount toggle',
                requiresAdmin: true
              }
            },
            {
              path: 'dropdownitems',
              name: 'SettingsDropdownItems',
              component: DropdownItems,
              meta: {
                label: 'Dropdown Items',
                description: 'Manage option dictionaries with fast add and remove actions',
                requiresAdmin: true
              }
            },
            {
              path: 'domains',
              name: 'SettingsDomains',
              component: SettingsDomains,
              meta: {
                label: 'Domains',
                description: 'Namecheap-managed domains and DNS records',
                allowedRoleGroups: ['superadmin']
              }
            },
            {
              path: 'domains/:domain/dns',
              name: 'SettingsDomainDns',
              component: SettingsDomainDns,
              meta: {
                label: 'Domain DNS',
                description: 'Edit DNS host records for a domain',
                allowedRoleGroups: ['superadmin']
              }
            },
          ]
        },
        {
          path: 'notes',
          meta: { label: 'Notes' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Notes,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'create',
              name: 'Create Note',
              component: CreateNote,
              meta: {
                label: 'Create Note',
                requiresUser: true
              }
            },
            {
              path: ':id',
              name: 'Note',
              component: Note,
              meta: {
                label: 'Note Details',
                requiresUser: true
              }
            },
            {
              path: ':id/edit',
              name: 'Edit Note',
              component: EditNote,
              meta: {
                label: 'Edit Note',
                requiresUser: true
              }
            },
          ]
        },
        {
          path: 'roles',
          meta: { label: 'Roles' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Roles,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: 'create',
              name: 'Create Role',
              component: CreateRole,
              meta: {
                label: 'Create Role',
                requiresAdmin: true
              }
            },
            {
              path: ':id',
              name: 'Role',
              component: Role,
              meta: {
                label: 'Role Details',
                requiresAdmin: true
              }
            },
            {
              path: ':id/edit',
              name: 'Edit Role',
              component: EditRole,
              meta: {
                label: 'Edit Role',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          path: 'bread',
          meta: { label: 'Bread' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Breads,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: 'create',
              name: 'CreateBread',
              component: CreateBread,
              meta: {
                label: 'Create Bread',
                requiresAdmin: true
              }
            },
            {
              path: ':id',
              name: 'Bread',
              component: Bread,
              meta: {
                label: 'Bread Details',
                requiresAdmin: true
              }
            },
            {
              path: ':id/edit',
              name: 'Edit Bread',
              component: EditBread,
              meta: {
                label: 'Edit Bread',
                requiresAdmin: true
              }
            },
            {
              path: ':id/delete',
              name: 'Delete Bread',
              component: DeleteBread,
              meta: {
                label: 'Delete Bread',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          path: 'email',
          meta: { label: 'Emails' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Emails,
              meta: {
                requiresAdmin: true
              }
            },
            {
              path: 'create',
              name: 'Create Email Template',
              component: CreateEmail,
              meta: {
                label: 'Create Email Template',
                requiresAdmin: true
              }
            },
            {
              path: ':id',
              name: 'Show Email Tempalte',
              component: ShowEmail,
              meta: {
                label: 'Show Email Template',
                requiresAdmin: true
              }
            },
            {
              path: ':id/edit',
              name: 'Edit Email Template',
              component: EditEmail,
              meta: {
                label: 'Edit Email Tempalate',
                requiresAdmin: true
              }
            },
            {
              path: ':id/sendEmail',
              name: 'Send Email',
              component: SendEmail,
              meta: {
                label: 'Send Email',
                requiresAdmin: true
              }
            },
          ]
        },
        {
          path: 'resource',
          meta: { label: 'Resources' },
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: ':bread/resource',
              component: Resources,
            },
            {
              path: ':bread/resource/create',
              meta: { label: 'Create Resource' },
              name: 'CreateResource',
              component: CreateResource
            },
            {
              path: ':bread/resource/:id',
              meta: { label: 'Resource Details' },
              name: 'Resource',
              component: Resource,
            },
            {
              path: ':bread/resource/:id/edit',
              meta: { label: 'Edit Resource' },
              name: 'Edit Resource',
              component: EditResource
            },
            {
              path: ':bread/resource/:id/delete',
              meta: { label: 'Delete Resource' },
              name: 'Delete Resource',
              component: DeleteResource
            },
          ]
        },
        {
          path: 'base',
          redirect: '/base/cards',
          name: 'Base',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'cards',
              name: 'Cards',
              component: Cards,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'forms',
              name: 'Forms',
              component: Forms,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'switches',
              name: 'Switches',
              component: Switches,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'tables',
              name: 'Tables',
              component: Tables,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'tabs',
              name: 'Tabs',
              component: Tabs,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'breadcrumb',
              name: 'Breadcrumb',
              component: Breadcrumbs,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'carousel',
              name: 'Carousel',
              component: Carousels,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'collapse',
              name: 'Collapse',
              component: Collapses,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'jumbotron',
              name: 'Jumbotron',
              component: Jumbotrons,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'list-group',
              name: 'List Group',
              component: ListGroups,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'navs',
              name: 'Navs',
              component: Navs,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'navbars',
              name: 'Navbars',
              component: Navbars,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'pagination',
              name: 'Pagination',
              component: Paginations,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'popovers',
              name: 'Popovers',
              component: Popovers,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'progress',
              name: 'Progress',
              component: ProgressBars,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'tooltips',
              name: 'Tooltips',
              component: Tooltips,
              meta: {
                requiresUser: true
              }
            }
          ]
        },
        {
          path: 'buttons',
          redirect: '/buttons/standard-buttons',
          name: 'Buttons',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'buttons',
              name: 'Standard Buttons',
              component: StandardButtons,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'button-group',
              name: 'Button Group',
              component: ButtonGroups,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'dropdowns',
              name: 'Dropdowns',
              component: Dropdowns,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'brand-buttons',
              name: 'Brand Buttons',
              component: BrandButtons,
              meta: {
                requiresUser: true
              }
            }
          ]
        },
        {
          path: 'icon',
          redirect: '/icons/coreui-icons',
          name: 'CoreUI Icons',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'coreui-icons',
              name: 'Icons library',
              component: CoreUIIcons,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'brands',
              name: 'Brands',
              component: Brands,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'flags',
              name: 'Flags',
              component: Flags,
              meta: {
                requiresUser: true
              }
            }
          ]
        },
        {
          path: 'notifications',
          redirect: '/notifications/alerts',
          name: 'Notifications',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'alerts',
              name: 'Alerts',
              component: Alerts,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'badge',
              name: 'Badge',
              component: Badges,
              meta: {
                requiresUser: true
              }
            },
            {
              path: 'modals',
              name: 'Modals',
              component: Modals,
              meta: {
                requiresUser: true
              }
            }
          ]
        }
      ]
    },
    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render(c) { return c('router-view') }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '403',
          name: 'Page403',
          component: Page403
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
      ]
    },
    {
      path: '/',
      redirect: '/login',
      name: 'Auth',
      component: {
        render(c) { return c('router-view') }
      },
      children: [
        {
          path: 'login',
          name: 'Login',
          component: Login
        },
        {
          path: 'register',
          name: 'Register',
          component: Register
        },
      ]
    },
    {
      path: '*',
      name: '404',
      component: Page404
    }
  ]
}
